// Test Definitions for System Health Dashboard
// Organized by Application Sidebar Structure

// ========== OPERATIONS SECTION ==========

export const rolesTests = [
  { id: 'create_role', name: 'Create Role' },
  { id: 'create_role_permission', name: 'Create Role with Permission' },
  { id: 'update_role_name', name: 'Update Role Name' },
  { id: 'update_role_permission', name: 'Update Role Permission' },
  { id: 'delete_role', name: 'Delete Role' }
];

export const teamTests = [
  { id: 'create_team', name: 'Create Team Member' },
  { id: 'create_team_role', name: 'Create Team Member with Role' },
  { id: 'update_team_name', name: 'Update Team Member Name' },
  { id: 'update_team_role', name: 'Update Team Member Role' },
  { id: 'delete_team', name: 'Delete Team Member' }
];

export const assetsTests = [
  { id: 'create_asset', name: 'Create Asset' },
  { id: 'create_asset_team_req', name: 'Create Asset with Team Requirements' },
  { id: 'update_asset_name', name: 'Update Asset Name' },
  { id: 'update_team_requirements', name: 'Update Asset Team Requirements' },
  { id: 'delete_asset', name: 'Delete Asset' }
];

export const servicesTests = [
  { id: 'create_service', name: 'Create Service' },
  { id: 'update_name_price', name: 'Update Service Name & Price' },
  { id: 'update_dynamic_pricing', name: 'Update Dynamic Pricing' },
  { id: 'update_time_slots', name: 'Update Time Slots & Availability' },
  { id: 'upload_service_photo', name: 'Upload Service Photo' },
  { id: 'delete_service', name: 'Delete Service' }
];

export const bookingsTests = [
  { id: 'create_booking', name: 'Create Booking (Valid)' },
  { id: 'view_booking', name: 'View Booking Details' },
  { id: 'update_booking', name: 'Update Booking Date/Time' },
  { id: 'cancel_booking', name: 'Cancel Booking' },
  { id: 'availability_check', name: 'Real-Time Availability Check' },
  { id: 'double_booking_prevention', name: 'Double Booking Prevention' },
  { id: 'time_conflict', name: 'Time Slot Conflict Detection' },
  { id: 'date_navigation', name: 'Date Navigation (Forward/Backward)' },
  { id: 'per_ticket_selection', name: 'Per-Ticket Service Selection' },
  { id: 'multi_asset_availability', name: 'Multi-Asset Availability' },
  { id: 'booking_state_transitions', name: 'Booking State Transitions' },
  { id: 'no_show_marking', name: 'No-Show Marking' }
];

export const flowsTests = [
  { id: 'trigger_booking_created', name: 'Trigger Flow on Booking Created' },
  { id: 'trigger_payment_completed', name: 'Trigger Flow on Payment Completed' },
  { id: 'trigger_booking_cancelled', name: 'Trigger Flow on Booking Cancelled' },
  { id: 'multi_step_flow', name: 'Multi-Step Flow Execution' },
  { id: 'flow_condition_logic', name: 'Flow Condition Logic' },
  { id: 'flow_error_handling', name: 'Flow Error Handling' }
];

// ========== FINANCIALS SECTION ==========

export const revenueTests = [
  { id: 'calculate_total_revenue', name: 'Calculate Total Revenue by Date Range' },
  { id: 'revenue_by_service', name: 'Revenue by Service Breakdown' },
  { id: 'revenue_by_payment_method', name: 'Revenue by Payment Method' },
  { id: 'generate_monthly_report', name: 'Generate Monthly Revenue Report' },
  { id: 'export_revenue_csv', name: 'Export Revenue to CSV' },
  { id: 'revenue_trend_analysis', name: 'Revenue Trend Analysis' }
];

export const payoutsTests = [
  { id: 'calculate_pending_payouts', name: 'Calculate Pending Payouts' },
  { id: 'process_payout', name: 'Process Payout to Bank Account' },
  { id: 'payout_status_tracking', name: 'Payout Status Tracking' },
  { id: 'minimum_payout_threshold', name: 'Minimum Payout Threshold' },
  { id: 'stripe_balance_check', name: 'Stripe Connect Balance Check' },
  { id: 'payout_failure_handling', name: 'Payout Failure Handling' }
];

export const accountTests = [
  { id: 'update_business_name', name: 'Update Business Name' },
  { id: 'update_business_address', name: 'Update Business Address' },
  { id: 'update_tax_settings', name: 'Update Tax Settings' },
  { id: 'update_payment_processor', name: 'Update Payment Processor Settings' }
];

export const payrollTests = [
  { id: 'calculate_team_hours', name: 'Calculate Team Member Hours' },
  { id: 'generate_payroll_report', name: 'Generate Payroll Report' },
  { id: 'process_team_payment', name: 'Process Team Member Payment' },
  { id: 'payroll_tax_calculation', name: 'Payroll Tax Calculation' },
  { id: 'payroll_history_export', name: 'Payroll History Export' },
  { id: 'pay_stub_generation', name: 'Pay Stub Generation' }
];

export const stripeConnectTests = [
  { id: 'payment_intent_creation', name: 'Stripe Payment Intent Creation' },
  { id: 'successful_payment_webhook', name: 'Successful Payment Webhook' },
  { id: 'failed_payment_handling', name: 'Failed Payment Handling' },
  { id: 'refund_processing', name: 'Refund Processing' },
  { id: 'amount_matches_total', name: 'Amount Matches Booking Total' },
  { id: 'currency_validation', name: 'Currency Validation' },
  { id: 'payment_status_sync', name: 'Payment Status Sync with Booking' },
  { id: 'duplicate_payment_prevention', name: 'Duplicate Payment Prevention' }
];

// ========== CUSTOMERS SECTION ==========

export const brandingTests = [
  { id: 'upload_logo', name: 'Upload Logo' },
  { id: 'update_brand_colors', name: 'Update Brand Colors' },
  { id: 'customize_email_templates', name: 'Customize Email Templates' },
  { id: 'preview_brand_changes', name: 'Preview Brand Changes' }
];

export const waiversTests = [
  { id: 'create_waiver', name: 'Create Waiver Template' },
  { id: 'update_waiver', name: 'Update Waiver Template' },
  { id: 'activate_waiver', name: 'Activate/Deactivate Waiver' },
  { id: 'delete_waiver', name: 'Delete Waiver Template' },
  { id: 'sign_waiver', name: 'Customer Sign Waiver' },
  { id: 'retrieve_signed_waiver', name: 'Retrieve Signed Waiver' },
  { id: 'waiver_required_booking', name: 'Waiver Required Before Booking' },
  { id: 'export_signed_waivers', name: 'Export Signed Waivers' }
];

export const reviewsTests = [
  { id: 'customer_submit_review', name: 'Customer Submit Review' },
  { id: 'review_rating_validation', name: 'Review Rating Validation (1-5 stars)' },
  { id: 'review_text_sanitization', name: 'Review Text Sanitization' },
  { id: 'view_all_reviews', name: 'View All Reviews' },
  { id: 'respond_to_review', name: 'Respond to Review' },
  { id: 'flag_inappropriate_review', name: 'Flag Inappropriate Review' }
];

export const checkinTests = [
  { id: 'generate_checkin_qr', name: 'Generate Check-In QR Code' },
  { id: 'scan_qr_code', name: 'Customer Scan QR Code' },
  { id: 'mark_checked_in', name: 'Mark Customer as Checked-In' },
  { id: 'late_checkin_detection', name: 'Late Check-In Detection' },
  { id: 'checkin_history', name: 'Check-In History' },
  { id: 'no_show_after_window', name: 'No-Show After Check-In Window' }
];

export const leadsTests = [
  { id: 'capture_lead_widget', name: 'Capture Lead from Widget' },
  { id: 'assign_lead_team', name: 'Assign Lead to Team Member' },
  { id: 'convert_lead_customer', name: 'Convert Lead to Customer' },
  { id: 'lead_status_updates', name: 'Lead Status Updates' },
  { id: 'lead_source_attribution', name: 'Lead Source Attribution' },
  { id: 'lead_followup_reminders', name: 'Lead Follow-Up Reminders' }
];

export const relationsTests = [
  { id: 'create_customer_profile', name: 'Create Customer Profile' },
  { id: 'update_customer_contact', name: 'Update Customer Contact Info' },
  { id: 'view_customer_history', name: 'View Customer Booking History' },
  { id: 'add_customer_notes', name: 'Add Customer Notes' },
  { id: 'customer_lifetime_value', name: 'Customer Lifetime Value' },
  { id: 'customer_segmentation', name: 'Customer Segmentation' }
];

export const ticketsTests = [
  { id: 'create_ticket', name: 'Create Customer Ticket' },
  { id: 'assign_ticket_team', name: 'Assign Ticket to Team Member' },
  { id: 'update_ticket_status', name: 'Update Ticket Status' },
  { id: 'search_tickets', name: 'Search Tickets by Customer/Status' }
];

// ========== SETTINGS SECTION ==========

export const profileTests = [
  { id: 'update_owner_name', name: 'Update Owner Name' },
  { id: 'update_owner_email', name: 'Update Owner Email' },
  { id: 'change_password', name: 'Change Password' },
  { id: 'upload_profile_picture', name: 'Upload Profile Picture' }
];

export const preferencesTests = [
  { id: 'set_business_hours', name: 'Set Business Hours' },
  { id: 'set_timezone', name: 'Set Timezone' },
  { id: 'set_currency', name: 'Set Currency' },
  { id: 'set_language', name: 'Set Language' }
];

export const calendarTests = [
  { id: 'generate_ical_feed', name: 'Generate iCal Feed' },
  { id: 'subscribe_booking_calendar', name: 'Subscribe to Booking Calendar' },
  { id: 'update_calendar_booking_change', name: 'Update Calendar on Booking Change' },
  { id: 'calendar_url_security', name: 'Calendar URL Security' },
  { id: 'google_import_events', name: 'Google: Import Events' },
  { id: 'google_export_bookings', name: 'Google: Export Bookings' },
  { id: 'google_conflict_detection', name: 'Google: Conflict Detection' },
  { id: 'oauth_token_refresh', name: 'OAuth Token Refresh' }
];

export const webhooksTests = [
  { id: 'create_webhook_endpoint', name: 'Create Webhook Endpoint' },
  { id: 'test_webhook_delivery', name: 'Test Webhook Delivery' },
  { id: 'webhook_event_types', name: 'Webhook Event Types' },
  { id: 'webhook_signature_validation', name: 'Webhook Signature Validation' },
  { id: 'webhook_retry_logic', name: 'Webhook Retry Logic' },
  { id: 'webhook_delivery_logs', name: 'Webhook Delivery Logs' }
];

export const apiKeysTests = [
  { id: 'generate_api_key', name: 'Generate API Key' },
  { id: 'revoke_api_key', name: 'Revoke API Key' },
  { id: 'api_key_permissions', name: 'API Key Permissions Scoping' },
  { id: 'api_key_usage_tracking', name: 'API Key Usage Tracking' }
];

export const activityLogTests = [
  { id: 'log_user_actions', name: 'Log User Actions' },
  { id: 'filter_activity_user', name: 'Filter Activity by User' },
  { id: 'filter_activity_date', name: 'Filter Activity by Date Range' },
  { id: 'export_activity_log', name: 'Export Activity Log' }
];

// ========== AUTHENTICATION FLOWS ==========

export const ownerSigninTests = [
  { id: 'owner_login_success', name: 'Owner Login Success' },
  { id: 'owner_wrong_password', name: 'Owner Wrong Password Blocked' },
  { id: 'owner_session_management', name: 'Owner Session Management' }
];

export const teamSigninTests = [
  { id: 'create_with_role', name: 'Create Team Member with Role' },
  { id: 'login_success', name: 'Team Member Login' },
  { id: 'role_verification', name: 'Role ID Verification' },
  { id: 'permissions_accessible', name: 'Role Permissions Accessible' },
  { id: 'wrong_password', name: 'Wrong Password Blocked' }
];

export const signupTests = [
  { id: 'duplicate_email', name: 'Duplicate Email Detection' },
  { id: 'email_function', name: 'Email Send Function (*)' },
  { id: 'pin_verification', name: 'PIN Verification Logic' },
  { id: 'slug_availability', name: 'Slug Availability Check' },
  { id: 'html_sanitization', name: 'HTML Sanitization' }
];

// ========== CUSTOMER FLOWS ==========

export const widgetCheckoutTests = [
  { id: 'widget_loads', name: 'Widget Loads on External Site' },
  { id: 'select_service', name: 'Select Service from Widget' },
  { id: 'select_datetime', name: 'Select Date/Time from Widget' },
  { id: 'enter_customer_details', name: 'Enter Customer Details' },
  { id: 'accept_waiver', name: 'Accept Waiver (if required)' },
  { id: 'process_payment', name: 'Process Payment via Stripe' },
  { id: 'booking_confirmation_displayed', name: 'Booking Confirmation Displayed' },
  { id: 'booking_confirmation_email', name: 'Booking Confirmation Email' }
];

export const waiverFlowTests = [
  { id: 'present_waiver', name: 'Present Waiver to Customer' },
  { id: 'digital_signature', name: 'Capture Digital Signature' },
  { id: 'store_signed_waiver', name: 'Store Signed Waiver Securely' },
  { id: 'email_signed_copy', name: 'Email Signed Copy to Customer' }
];

export const qrCodeScanningTests = [
  { id: 'generate_unique_qr', name: 'Generate Unique QR per Booking' },
  { id: 'scan_qr_mobile', name: 'Scan QR Code with Mobile' },
  { id: 'validate_qr_code', name: 'Validate QR Code' },
  { id: 'update_checkin_status', name: 'Update Check-In Status' },
  { id: 'expired_qr_rejection', name: 'Expired QR Code Rejection' },
  { id: 'duplicate_scan_prevention', name: 'Duplicate Scan Prevention' }
];
